import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle, Clock, Database, Mail, RefreshCw } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { apiService, formatApiError } from '../services/api';

const StatusBadge = ({ status }) => {
  const color = status === 'healthy' ? 'bg-green-100 text-green-800' : status === 'degraded' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
  return <Badge className={color} data-testid={`status-${status}`}>{String(status).toUpperCase()}</Badge>;
};

const AdminMonitoring = () => {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);
  const [error, setError] = useState('');

  const load = () => apiService.admin.getMonitoring({ hours }).then((r) => { setData(r.data.data); setError(''); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { load(); }, [hours]);

  if (error) return <p className="text-red-600" data-testid="monitoring-error">{error}</p>;
  if (!data) return <p className="text-gray-500" data-testid="monitoring-loading">Loading monitoring…</p>;

  const c = data.counts || {};
  return (
    <div className="space-y-6" data-testid="admin-monitoring">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold flex items-center"><Activity className="w-5 h-5 mr-2" />System monitoring (last {data.window_hours}h)</h2>
        <div className="flex items-center space-x-2">
          {[1, 24, 168].map((h) => <Button key={h} size="sm" variant={h === hours ? 'default' : 'outline'} onClick={() => setHours(h)}>{h === 168 ? '7d' : `${h}h`}</Button>)}
          <Button size="sm" variant="outline" onClick={load} data-testid="monitoring-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4"><p className="text-xs text-gray-500 flex items-center"><Database className="w-3 h-3 mr-1" />Database</p><div className="mt-2 flex items-center justify-between"><StatusBadge status={data.database?.status} /><span className="text-xs text-gray-500">{data.database?.ping_ms ?? '–'} ms</span></div></Card>
        <Card className="p-4"><p className="text-xs text-gray-500 flex items-center"><Mail className="w-3 h-3 mr-1" />Email service</p><div className="mt-2"><StatusBadge status={data.email_service?.status} />{data.email_service?.reason && <p className="text-xs text-gray-500 mt-1">{data.email_service.reason}</p>}</div></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Backend exceptions</p><p className={`text-2xl font-bold ${data.error_rate_flag ? 'text-red-600' : ''}`} data-testid="count-backend-exceptions">{c.backend_exception || 0}</p>{data.error_rate_flag && <p className="text-xs text-red-600">High error rate</p>}</Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Client crashes</p><p className="text-2xl font-bold">{c.client_error || 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Auth failures</p><p className="text-2xl font-bold">{c.auth_failure || 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Email failures</p><p className="text-2xl font-bold">{c.email_failure || 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Rate-limited requests</p><p className="text-2xl font-bold">{c.rate_limited || 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-gray-500">Slow requests (&gt;1.5s)</p><p className="text-2xl font-bold">{c.slow_request || 0}</p></Card>
      </div>

      <Card className="p-4">
        <h3 className="font-semibold flex items-center mb-3"><AlertTriangle className="w-4 h-4 mr-2 text-red-500" />Recent errors</h3>
        {(data.recent_errors || []).length === 0 ? <p className="text-sm text-gray-500">No errors in this window.</p> : (
          <div className="space-y-2 text-sm">{data.recent_errors.map((e) => (
            <div key={e.id} className="border-b pb-2"><span className="text-gray-500 mr-2">{new Date(e.created_at).toLocaleString()}</span><Badge variant="outline" className="mr-2">{e.kind}</Badge>{e.message}{e.context?.request_id && <span className="text-xs text-gray-400 ml-2">req {e.context.request_id}</span>}</div>
          ))}</div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-semibold flex items-center mb-3"><Clock className="w-4 h-4 mr-2 text-yellow-600" />Slowest requests</h3>
        {(data.slowest_requests || []).length === 0 ? <p className="text-sm text-gray-500">No slow requests.</p> : (
          <div className="space-y-1 text-sm">{data.slowest_requests.map((e) => <div key={e.id}>{e.message}</div>)}</div>
        )}
      </Card>
      <p className="text-xs text-gray-500">External error tracking (Sentry): {data.sentry}. Set SENTRY_DSN in the backend environment to enable.</p>
    </div>
  );
};

export default AdminMonitoring;
